import {
  Grouping,
  TokenUsage,
  TokenUsageWithBreakdown,
} from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { TokenUsageType } from '../constants/tokenUsageType';
import { EnrichedWorkspace, FormattedTokenUsageDataPoint, User } from '../types';

/**
 * Normalizes a group ID by stripping any resource prefix if present.
 * The H2OGPTe API returns workspace groups with prefix (e.g., "workspaces/uuid")
 * but user groups as raw UUIDs. This function handles both cases to ensure
 * consistent lookup in the workspace/user maps.
 *
 * @param groupId The group identifier from the API response
 * @returns The normalized UUID without any resource prefix
 */
export function normalizeGroupId(groupId: string): string {
  // Check for workspaces/ prefix
  if (groupId.startsWith('workspaces/')) {
    return groupId.substring('workspaces/'.length);
  }
  // Check for users/ prefix (in case API format changes)
  if (groupId.startsWith('users/')) {
    return groupId.substring('users/'.length);
  }
  return groupId;
}

/**
 * Converts BigIntString to number
 */
export function convertBigIntStringToNumber(value: string | undefined): number {
  if (!value) return 0;
  return parseInt(value, 10);
}

/**
 * Extract token value based on usage type selection
 */
export function getTokenValueByType(usage: TokenUsage | undefined, tokenUsageType: TokenUsageType): number {
  const inputTokens = convertBigIntStringToNumber(usage?.inputTokens);
  const outputTokens = convertBigIntStringToNumber(usage?.outputTokens);

  switch (tokenUsageType) {
    case TokenUsageType.INPUT:
      return inputTokens;
    case TokenUsageType.OUTPUT:
      return outputTokens;
    case TokenUsageType.TOTAL:
    default:
      return inputTokens + outputTokens;
  }
}

/**
 * Resolves a group ID to a human-readable display name for H2OGPTe groupings
 *
 * @param groupId The group identifier (UUID or string, may include resource prefix)
 * @param groupBy The H2OGPTe grouping type (WORKSPACE, USER, MODEL)
 * @param workspacesMap Map of workspace UUIDs to workspace objects
 * @param usersMap Map of user UUIDs to user objects
 * @returns Human-readable display name
 */
export function resolveH2OGPTeGroupDisplayName(
  groupId: string,
  groupBy: Grouping,
  workspacesMap: Map<string, EnrichedWorkspace>,
  usersMap: Map<string, User>
): string {
  // Normalize the groupId to handle prefixed IDs from the API
  // H2OGPTe API returns workspace groups with prefix (e.g., "workspaces/uuid")
  const normalizedId = normalizeGroupId(groupId);

  if (groupBy === Grouping.WORKSPACE) {
    const workspace = workspacesMap.get(`workspaces/${normalizedId}`);
    if (workspace) {
      if (workspace.displayName === 'Personal Workspace') {
        return `PW - ${workspace.userDisplayName}`;
      } else {
        return workspace.displayName;
      }
    }
  } else if (groupBy === Grouping.USER) {
    // For user grouping, lookup user by "users/{uuid}"
    // Use normalizedId to handle any potential "users/" prefix from the API
    const lookupKey = `users/${normalizedId}`;
    const user = usersMap.get(lookupKey);

    if (user) {
      return user.displayName || normalizedId;
    }
  }
  // For MODEL grouping, keep the original groupId (model names are already human-readable)
  // Also fallback to normalizedId if lookup fails
  return normalizedId;
}

/**
 * Formats raw token usage data into a structure suitable for chart visualization
 *
 * @param usageData Raw token usage data from the API
 * @param groupBy The H2OGPTe grouping type
 * @param workspacesMap Map of workspace UUIDs to workspace objects
 * @param usersMap Map of user UUIDs to user objects
 * @param formatDate Function to format dates based on granularity
 * @param tokenUsageType Which token type to display (INPUT, OUTPUT, TOTAL)
 * @returns Object containing formatted data points and unique groups
 */
export function formatTokenUsageDataForChart(
  usageData: TokenUsageWithBreakdown[],
  groupBy: Grouping,
  workspacesMap: Map<string, EnrichedWorkspace>,
  usersMap: Map<string, User>,
  formatDate: (date: Date) => string,
  tokenUsageType: TokenUsageType = TokenUsageType.TOTAL
): { formattedData: FormattedTokenUsageDataPoint[]; groups: string[] } {
  // Collect all unique groups across all data points
  const allGroups = new Set<string>();

  // Use a Map to merge data points with the same formatted time
  const dataByTime = new Map<string, FormattedTokenUsageDataPoint>();

  usageData.forEach((datum) => {
    const usageBreakdown = datum.usageBreakdown || [];
    const time = (datum.interval?.startTime && formatDate(new Date(datum.interval.startTime))) || '';

    // Get or create data point for this time
    if (!dataByTime.has(time)) {
      dataByTime.set(time, { time });
    }
    const dataPoint = dataByTime.get(time)!;

    // Add/sum each group value
    usageBreakdown.forEach((breakdown) => {
      const groupId = breakdown.group || 'unknown';
      const tokenValue = getTokenValueByType(breakdown.usage, tokenUsageType);

      // Replace UUID with readable name based on grouping type
      const displayName = resolveH2OGPTeGroupDisplayName(groupId, groupBy, workspacesMap, usersMap);

      // Sum if already exists, otherwise set
      const existingValue = dataPoint[displayName];
      dataPoint[displayName] = (typeof existingValue === 'number' ? existingValue : 0) + tokenValue;
      allGroups.add(displayName);
    });
  });

  // Convert map to array, preserving insertion order
  const formattedData = Array.from(dataByTime.values());

  return {
    formattedData,
    groups: Array.from(allGroups),
  };
}
